import {
	type AclEntityMinInfoEntityTypeEnum,
	EntityStatus,
	type EventInvestmentType,
	PdfControllerApiFactory,
	type SmartXSyncRecordDtoStepEnum,
} from "@eu.mdotm.api/sphere";
import { StackingContext } from "@mdotm/shadcn/legacy/components";
import { DatePickerInputCtx } from "@mdotm/shadcn/mdotm/components";
import { SpawnMountPoint } from "@mdotm/shadcn/legacy/react-extensions";
import { readAtom, useAtomValue, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { noop, switchExpr, unpromisify } from "@mdotm/shadcn/legacy/utils";
import { useAsync } from "@mdotm/utils/promise";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { reportPlatformError, runWithErrorReporting } from "$root/api/error-reporting/report";
import { useApiGen } from "$root/api/hooks";
import { DataDisplayOverlayProvider } from "$root/components/DataDisplayOverlay";
import { ImpersonationBanner } from "$root/components/ImpersonateBox/ImpersonationBanner";
import InfiniteLoader from "$root/components/InfiniteLoader";
import { IdentityTransitionGate } from "$root/components/IdentityTransitionGate";
import PlatformRouter from "$root/components/PlatformRouter";
import { platformLayoutOptions$ } from "$root/components/PlatformRouter/layout-options";
import { typedUrlForRoute, useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { useEventBus, useGroupEventBus } from "$root/event-bus";
import { instrumentCommentaryGenerationCode } from "$root/functional-areas/instruments/Instrument-customisation";
import { usePropagationDone } from "$root/functional-areas/instruments/propagation";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { keyedBlockedPositions } from "$root/functional-areas/portfolio/smartx-blocked-positions";
import { accessToken$, impersonateEmail$ } from "$root/functional-areas/session/atom";
import { user$, useUserValue } from "$root/functional-areas/user";
import { authSessionInit, cleanupSession, logout } from "$root/functional-areas/user/auth";
import { getValidOidcUser, tokenFromOidcUser } from "$root/functional-areas/user/oidc";
import useConnectivity from "$root/hooks/useConnectivity";
import { formatDate, getLocalizedDateMask, parseDateInputToDay } from "$root/localization/formatters";
import { useLocaleFormatters } from "$root/localization/hooks";
import { dismissPlatformToast, platformToast, ToastContent, ToastMountPoint } from "$root/notification-system/toast";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { useQueryNoRefetch, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { downloadFileResponse, useSearchParams } from "$root/utils";
import { FunctionalAreasProvider } from "./context";

function App(): React.JSX.Element {
	const user = useAtomValue(user$);

	const { t } = useTranslation();

	const [runAutoLogin, autoLoginState] = useAsync(async () => {
		// The callback page completes the code exchange and hydrates the session itself.
		if (window.location.pathname.startsWith("/auth/callback")) {
			return;
		}
		const oidcUser = await getValidOidcUser();
		const token = tokenFromOidcUser(oidcUser);
		if (!token) {
			// No credential to restore, yet storage can still claim a signed-in user — an
			// identity left by an older build, one whose OIDC session is gone, or one another
			// tab wrote to the same key before this session ended (`user$` shares the key
			// across tabs, it just does not mirror changes between them). `Login`
			// redirects a "signed in" user away, so leaving that claim standing locks the user
			// out of the form until some request happens to 401 (the MDOT-24592 lockout).
			// Settle it here, where it needs neither a request nor a backend.
			if (readAtom(user$).signedIn) {
				// `cleanupSession` does not touch this, and a leftover impersonation must never
				// outlive the session that authorized it.
				writeAtom(impersonateEmail$, null);
				cleanupSession();
			}
			return;
		}
		try {
			await authSessionInit(token);
		} catch (err) {
			console.error(err);
			// Hydration never completed: there is no identity to attribute a pending write to, and
			// nothing has rendered that could be holding one.
			await logout({ credentialLost: true });
		}
	});
	const autoLoginRunRef = useUnsafeUpdatedRef(runAutoLogin);
	useEffect(() => {
		if (autoLoginState.kind === "initial") {
			autoLoginRunRef.current().catch(noop);
		}
	}, [autoLoginState.kind, autoLoginRunRef]);

	useEventBus(
		"unauthorized",
		() => {
			// Raised only once the session is beyond saving: the 401 interceptor in
			// `auth-interceptors` has already de-duplicated the renew, retried the transient
			// failures and replayed the request, so there is nothing left to try here.
			//
			// Re-check the session rather than relying on the subscribe-time guard: only log out
			// (which redirects to the Cognito Hosted UI `/logout`) if there was actually a
			// session. A stray 401 while signed out — e.g. sitting on the login page — must NOT
			// bounce the browser to a pool's `/logout` before the user has even chosen one.
			//
			// The credential is checked alongside the identity because `logout` is **browser-wide**:
			// it removes the shared OIDC user and ends the Hosted UI session for every open tab.
			// Only a tab that actually holds the bearer token has a session of its own to end.
			// A tab holding an identity but no token — one behind the MFA gate, where the token is
			// held out of `accessToken$` on purpose, or one whose hydration failed part-way — has
			// nothing to revoke, and tearing the browser down from there would end a session
			// another tab may be using perfectly happily. Settle locally instead and let the route
			// guards take the user to `/login`.
			if (!user.signedIn || readAtom(accessToken$) == null) {
				// `logout` is what normally clears this, and it does not run here. Drop the
				// impersonation anyway: it is `localStorage`-backed, so a leftover would keep
				// the interceptor stamping `X-Impersonate-Email` (see `auth-interceptors`) and re-show
				// the banner the moment anything marks the user signed in again. Safe even if
				// this races a sign-in that is still completing — `beginSignin` clears the
				// same atom, since a fresh sign-in must never inherit an impersonation.
				writeAtom(impersonateEmail$, null);
				// An identity with no credential behind it is not a session, and leaving it
				// standing leaves the shell rendering and 401ing on everything it asks for.
				if (user.signedIn) {
					cleanupSession();
				}
				return;
			}
			// The session is beyond saving (see above), so its writes have nothing left to authenticate
			// them and settling them would only delay the redirect.
			logout({ credentialLost: true })
				.then(() => {
					platformToast({
						children: t("appbar.SESSION_EXPIRED"),
						severity: "error",
						icon: "Icon-full-alert",
					});
				})
				.catch(noop);
		},
		{
			disabled: autoLoginState.kind === "initial" || autoLoginState.kind === "loading",
		},
	);

	const platformLayoutOptions = useAtomValue(platformLayoutOptions$);
	const { formatDateForInput, locale } = useLocaleFormatters();

	if (autoLoginState.kind === "initial" || autoLoginState.kind === "loading") {
		return <InfiniteLoader style={{ minHeight: "100dvh" }} />;
	}

	return (
		<>
			{platformLayoutOptions.mode !== "print" && <ToastMountPoint />}
			{platformLayoutOptions.mode !== "print" && <ImpersonationBanner />}
			<DataDisplayOverlayProvider>
				<DatePickerInputCtx.Provider
					value={{
						dateLocalizer: {
							placeholder: getLocalizedDateMask(locale),
							// A day, not an instant: `CalendarProps` documents every value the date controls carry as
							// `YYYY-MM-DD`, and the calendar half of them emits exactly that. `parseDate` used to sit
							// here and answers with `toISOString()`, so the typed half answered with an instant — and
							// one a day early anywhere east of Greenwich, where local midnight belongs to the previous
							// UTC day. Picking and typing the same day produced two different values.
							fromLocale: (localizedStr) => parseDateInputToDay(localizedStr, locale),
							toLocale: formatDateForInput,
						},
					}}
				>
					<StackingContext.Provider value={{ zIndex: 1000 }}>
						<FunctionalAreasProvider>
							<EventBusHandler />
							<IdentityTransitionGate>
								<PlatformRouter />
								<DownloadFromQueryParam />
								<ConnectivityNotification />
								<StackingContext.Provider value={{ zIndex: 2000 }}>
									<SpawnMountPoint />
								</StackingContext.Provider>
							</IdentityTransitionGate>
						</FunctionalAreasProvider>
						{/* <RemoteStorageDebugger /> */}
					</StackingContext.Provider>
				</DatePickerInputCtx.Provider>
			</DataDisplayOverlayProvider>
		</>
	);
}

/** @internal — exported for tests */
export function DownloadFromQueryParam(): ReactNode {
	const { zipId } = useSearchParams<"zipId">();
	const user = useUserValue();
	const pdfControllerV3Api = useApiGen(PdfControllerApiFactory);
	const query = useQueryNoRefetch(["queryGeneratedReport", zipId, user], {
		enabled: zipId !== undefined && user.signedIn,
		queryFn: async () => {
			const zip = await axiosExtract(pdfControllerV3Api.downloadReportsById(zipId!));
			return zip;
		},
	});

	useQueryReactor(query, {
		onError: (error: { response: { status: number }; message: string }) => {
			if (error.response.status === 400) {
				platformToast({
					children: "Unable to find an existing report in your account",
					icon: "Portfolio",
					severity: "error",
				});
			} else {
				platformToast({
					children: "Something went wrong, please try later",
					icon: "Portfolio",
					severity: "error",
				});
			}

			reportPlatformError(error, "ERROR", "portfolio", {
				message: "unable to download the generated zip",
			});
		},
		onSuccess: (zip) => {
			downloadFileResponse(zip, {
				fileName: `${user.name ? `${user.name}_` : ""}Reports_${formatDate(new Date())}.zip`,
			});
		},
	});

	return null;
}

/** Where a SmartX sync stopped, as a notification says it. */
const smartXSyncStepLabels: Record<SmartXSyncRecordDtoStepEnum, string> = {
	COMPOSITION_CHECK: "checking the composition",
	LOGIN: "signing in to SmartX",
	MODEL_CHECK: "checking the SmartX model",
	SYMBOL_RESOLUTION: "looking up the tickers on SmartX",
	CURRENT_HOLDINGS: "reading the model's holdings",
	HOLDINGS_UPDATE: "updating the holdings",
};

/** Blocked positions a SmartX notification lists before summing up the rest. */
const SMARTX_TOAST_MAX_BLOCKED = 5;

/**
 * A component that can be used to globally subscribe to events dispatched on
 * the application event bus and react to them.
 *
 * @internal — exported for tests
 */
export function EventBusHandler() {
	const { push } = useTypedNavigation();

	function routeByInvestment(investment?: EventInvestmentType) {
		if (!investment) {
			return;
		}

		switch (investment) {
			case "PORTFOLIO_IMPORT":
			case "PORTFOLIO_UPDATE":
			case "PORTFOLIO_UPLOAD":
			case "INVESTMENT_DRAFT_CONFIGURATION_UPDATE":
				return (uuid: string, proposal?: boolean) =>
					push("PortfolioDetails", {
						portfolioUid: uuid,
						proposal: String(proposal),
					});
			case "REFERENCE_UPDATE":
				return (uuid: string) => push("ReferenceDetails", { uuid });
			case "UNIVERSE_UPDATE":
				return (uuid: string) => push("UniverseDetails", { universeUuid: uuid });
			case "INVESTMENT_REPORT_UPDATE":
			case "NONE":
				return () => undefined;
			default:
				return () => undefined;
		}
	}

	const FunctionaAreaSSE: Record<
		Exclude<EventInvestmentType, "INVESTMENT_DRAFT_CONFIGURATION_UPDATE" | "PORTFOLIO_UPLOAD">,
		{ urlGenerator: (uuid: string) => string; typeLabel: string }
	> = useMemo(
		() => ({
			PORTFOLIO_IMPORT: {
				urlGenerator: (uuid: string) => typedUrlForRoute("PortfolioDetails", { portfolioUid: uuid }),
				typeLabel: "portfolio",
			},
			PORTFOLIO_UPDATE: {
				urlGenerator: (uuid: string) => typedUrlForRoute("PortfolioDetails", { portfolioUid: uuid }),
				typeLabel: "portfolio",
			},
			PORTFOLIO_UPLOAD: {
				urlGenerator: (uuid: string) => typedUrlForRoute("PortfolioDetails", { portfolioUid: uuid }),
				typeLabel: "portfolio",
			},
			UNIVERSE_UPDATE: {
				urlGenerator: (uuid: string) => typedUrlForRoute("UniverseDetails", { universeUuid: uuid }),
				typeLabel: "universe",
			},
			REFERENCE_UPDATE: {
				urlGenerator: (uuid: string) => typedUrlForRoute("ReferenceDetails", { uuid }),
				typeLabel: "reference",
			},
			INVESTMENT_DRAFT_CONFIGURATION_UPDATE: {
				urlGenerator: (uuid: string) => typedUrlForRoute("PortfolioDetails", { portfolioUid: uuid }),
				typeLabel: "draft",
			},
			NONE: {
				typeLabel: "",
				urlGenerator: () => "/",
			},
			INVESTMENT_REPORT_UPDATE: {
				typeLabel: "",
				urlGenerator: () => "/",
			},
		}),
		[],
	);

	useEventBus("benchmark-update", (e) => {
		if (e.level === "INFO" && e.type === "REFERENCE_UPDATE" && e.status === "CALCULATING") {
			return;
		}

		if (e.level === "INFO" && e.type === "REFERENCE_UPDATE") {
			platformToast({
				children: (
					<ToastContent onClick={() => push("LegacyBenchmark", { uuid: e.uuid ?? "" })}>
						Sphere has updated your <strong>{e.name}</strong> benchmark.
					</ToastContent>
				),
				severity: "info",
				autoClose: 15000,
				icon: "Portfolio",
			});
		}
	});
	useEventBus("investment-update", (e) => {
		if ((e && e.type === "INVESTMENT_DRAFT_CONFIGURATION_UPDATE") || e.type === undefined) {
			return;
		}

		if (e.status === "CALCULATING") {
			return;
		}

		if (e.status === "REVIEW") {
			platformToast({
				children: (
					<ToastContent onClick={() => routeByInvestment(e.type)?.(e.uuid!)}>
						The <strong>{e.name}</strong> portfolio needs a review. There are some instruments that need to be updated.{" "}
						<u>Click here to see more</u>.
					</ToastContent>
				),
				severity: "info",
				autoClose: 15000,
				icon: "Portfolio",
			});
			return;
		}

		if (e.hasBreachedAlerts && e.status && e.status !== EntityStatus.Error && e.status !== EntityStatus.Executing) {
			platformToast({
				children: (
					<ToastContent onClick={() => routeByInvestment(e.type)?.(e.uuid!, e.status === EntityStatus.ProposalReady)}>
						The <strong>{e.name}</strong> portfolio is ready. Please be aware that there are some conflicting
						constraints in your portfolio. <u>Click here to see more</u>.
					</ToastContent>
				),
				severity: "warning",
				autoClose: 15000,
				icon: "Portfolio",
			});

			return;
		}

		if (e.level === "ERROR") {
			// Temporarily (MDOT-18271) mask Error status as Calculating until BE fixes their process
			// platformToast({
			// 	children: (
			// 		<ToastContent onClick={() => routeByInvestment(e.type)?.(e.uuid!)}>
			// 			The <strong>{e.name}</strong> is on error <u>Click here to see more</u>.
			// 		</ToastContent>
			// 	),
			// 	severity: "error",
			// 	autoClose: 15000,
			// 	icon: "Portfolio",
			// });

			return;
		}

		platformToast({
			children: (
				<ToastContent onClick={() => routeByInvestment(e.type)?.(e.uuid!)}>
					Sphere has updated your <strong>{e.name}</strong>{" "}
					{FunctionaAreaSSE[e.type as keyof typeof FunctionaAreaSSE].typeLabel}
				</ToastContent>
			),
			severity: "info",
			autoClose: 15000,
			icon: "Portfolio",
		});
	});

	useEventBus("proposal-execution-breached-constraint", (e) => {
		if (e.hasBreachedAlerts && e.status && e.status === EntityStatus.ProposalReady) {
			platformToast({
				children: (
					<ToastContent onClick={() => routeByInvestment(e.type)?.(e.uuid!, true)}>
						The <strong>{e.name}</strong> portfolio is on executing. Please be aware that there are some conflicting
						constraints in your portfolio. <u>Click here to see more</u>.
					</ToastContent>
				),
				severity: "info",
				autoClose: 15000,
				icon: "Portfolio",
			});
		}
	});

	useGroupEventBus(
		"commentary-update",
		(payload) => {
			if (payload.length === 1) {
				const [commentary] = payload;

				if (commentary.status === "READY") {
					platformToast({
						children: (
							<ToastContent onClick={() => routeByInvestment("PORTFOLIO_UPDATE")?.(commentary.uuid ?? "")}>
								Commentary of the portfolio: <strong>{commentary.name}</strong> is generated successfully.
							</ToastContent>
						),
						icon: "Portfolio",
						severity: "success",
					});
				}

				if (commentary.status === "ERROR") {
					platformToast({
						children: (
							<ToastContent onClick={() => routeByInvestment("PORTFOLIO_UPDATE")?.(commentary.uuid ?? "")}>
								Commentary of the portfolio: <strong>{commentary.name}</strong> is not generated successfully, try
								again.
							</ToastContent>
						),
						icon: "Portfolio",
						severity: "warning",
					});
				}
			}

			if (payload.length > 1) {
				const succededGeneration = payload.filter((x) => x.status === "READY");
				const failedGeneration = payload.filter((x) => x.status === "ERROR");

				if (succededGeneration.length > 0) {
					platformToast({
						children: `We have successfully generated ${succededGeneration.length} portfolio commentary`,
						icon: "Portfolio",
						severity: "success",
					});
				}

				if (failedGeneration.length > 0) {
					platformToast({
						children: `${failedGeneration.length} portfolio commentary generation have failed. Please try again.`,
						icon: "Portfolio",
						severity: "warning",
					});
				}
			}
		},
		{
			timeout: 10 * 1000,
		},
	);

	const onDownloadGeneratedZip = useCallback(
		(zipId: string, userName?: string) =>
			runWithErrorReporting(
				async () => {
					try {
						await portfolioRepository.downloadReportZip(zipId, {
							prefix: userName,
						});
					} catch (error) {
						throw new Error(String(error));
					}
				},
				{
					area: "portfolio",
					attemptedOperation: {
						message: "unable to download the generated zip",
						payload: JSON.stringify({ zipId, userName }),
					},
				},
			),
		[],
	);

	function generateDescription(portfolios: string[]) {
		if (portfolios.length <= 3) {
			return `Your report for “${portfolios.join(", ")}” is ready`;
		}

		const firstTreePortfolios = portfolios.slice(0, 2);
		const remainingPortfolios = portfolios.slice(2, portfolios.length);
		return `Your report for “${firstTreePortfolios.join(", ")} and ${remainingPortfolios.length} more...” is ready`;
	}

	useEventBus("bulk-report-download", (e) => {
		if (e.level === "INFO" && e.data) {
			platformToast({
				children:
					e.data.errorList && e.data.errorList.length > 0 ? (
						`Report could not be generated for “${e.data.errorList.join(", ")}”`
					) : (
						<ToastContent onClick={unpromisify(() => onDownloadGeneratedZip(e.data?.zipId ?? "", e.data?.userName))}>
							{e.data?.successList && (
								<>
									{generateDescription(e.data.successList)}{" "}
									<strong data-qualifier="BulkReportDownloadButton">
										<u>download</u>
									</strong>
								</>
							)}
						</ToastContent>
					),
				severity: "info",
				autoClose: 15000,
				icon: "Portfolio",
			});
			return;
		}

		platformToast({
			children: "Something went wrong while generating the report",
			severity: "error",
			autoClose: 15000,
			icon: "Portfolio",
		});
	});

	const roleByPermission = {
		editor: "editor",
		viewer: "viewer",
		owner: "owner",
	} as Record<string, string>;

	const areaByEntity = {
		BENCHMARK: "Benchmark",
		INVESTMENT: "Portfolio",
		MARKET_VIEW: "Market View",
		UNIVERSE: "Universe",
		GROUP_UNIVERSE: "Universe",
		REFERENCE: "Reference",
	} satisfies Record<AclEntityMinInfoEntityTypeEnum, string>;

	// "marketView", "portfolio", "benchmark", "universe", "reference"
	function generateEntityLink(entity: AclEntityMinInfoEntityTypeEnum, uuid?: string) {
		switchExpr(entity, {
			INVESTMENT: () => push("PortfolioDetails", { portfolioUid: uuid ?? "" }),
			REFERENCE: () => push("ReferenceDetails", { uuid: uuid ?? "" }),
			BENCHMARK: () => push("LegacyBenchmark", { uuid: uuid ?? "" }),
			MARKET_VIEW: () => push("MarketViewWorkSpace/View", { uuid: uuid ?? "" }),
			UNIVERSE: () => push("UniverseDetails", { universeUuid: uuid ?? "" }),
			GROUP_UNIVERSE: () => push("UniverseWithGroupsDetails", { universeUuid: uuid ?? "" }),
		});
	}

	function generateObjectPermission(ownerName?: string, permissionLevel?: string) {
		const role = roleByPermission[permissionLevel ?? "-"] ?? "-";

		return `${ownerName} has assigned you the ${role} role for`;
	}

	useEventBus("shared-entity", (e) => {
		if (e.level === "INFO") {
			return platformToast({
				children: (
					<ToastContent
						onClick={() => generateEntityLink(e.sharedEntityType as AclEntityMinInfoEntityTypeEnum, e.sharedEntityUuid)}
					>
						{generateObjectPermission(e.grantorName, e.permissionLevel)}
						&nbsp;
						<button type="button" className="underline">
							{e.sharedEntityName ?? "-"}
						</button>
					</ToastContent>
				),
				severity: "success",
				autoClose: 15000,
				icon: "Portfolio",
			});
		}

		platformToast({
			children: "Something went wrong while sharing the file",
			severity: "error",
			icon: "Portfolio",
		});
	});

	useEventBus("modified-entity", (e) => {
		if (e.level === "INFO") {
			return platformToast({
				children: (
					<ToastContent
						onClick={() => generateEntityLink(e.sharedEntityType as AclEntityMinInfoEntityTypeEnum, e.sharedEntityUuid)}
					>
						{`${e.grantorName} has edited`}
						&nbsp;
						<strong>{e.sharedEntityName ?? "-"}</strong>
						&nbsp;
						{e.sharedEntityType ? areaByEntity[e.sharedEntityType as AclEntityMinInfoEntityTypeEnum] : "-"}
					</ToastContent>
				),
				severity: "success",
				autoClose: 15000,
				icon: "Portfolio",
			});
		}

		platformToast({
			children: "Something went wrong while sharing the file",
			severity: "error",
			icon: "Portfolio",
		});
	});

	useEventBus("deleted-entity", (e) => {
		if (e.level === "INFO") {
			return platformToast({
				children: (
					<ToastContent>
						{`${e.grantorName} has deleted`}
						&nbsp;
						<strong>{e.sharedEntityName ?? "-"}</strong>
						&nbsp;
						{e.sharedEntityType ? areaByEntity[e.sharedEntityType as AclEntityMinInfoEntityTypeEnum] : "-"}
					</ToastContent>
				),
				severity: "info",
				autoClose: 15000,
				icon: "Portfolio",
			});
		}

		platformToast({
			children: "Something went wrong while sharing the file",
			severity: "error",
			icon: "Portfolio",
		});
	});

	useEventBus("removed-entity", (e) => {
		if (e.level === "INFO") {
			return platformToast({
				children: (
					<ToastContent
						onClick={() => generateEntityLink(e.sharedEntityType as AclEntityMinInfoEntityTypeEnum, e.sharedEntityUuid)}
					>
						{`${e.grantorName} has removed you from`}
						&nbsp;
						<strong>{e.sharedEntityName ?? "-"}</strong>
						&nbsp;
						{e.sharedEntityType ? areaByEntity[e.sharedEntityType as AclEntityMinInfoEntityTypeEnum] : "-"}
					</ToastContent>
				),
				severity: "info",
				autoClose: 15000,
				icon: "Portfolio",
			});
		}

		platformToast({
			children: "Something went wrong while sharing the file",
			severity: "error",
			icon: "Portfolio",
		});
	});

	useGroupEventBus("instrument-commentary-update", (commentaries) => {
		const succeededCommentaries = commentaries.filter((x) => x.level === "INFO");
		const commentaryServerError = commentaries.filter(
			(x) =>
				x.level === "ERROR" && x.descriptionStatus === instrumentCommentaryGenerationCode.COMMENTARY_GENERATION_ERROR,
		);

		const missingDataError = commentaries.filter(
			(x) => x.level === "ERROR" && x.descriptionStatus === instrumentCommentaryGenerationCode.DATA_NOT_AVAILABLE,
		);

		if (succeededCommentaries.length > 0) {
			const singleCommentaryCopy = "We have generated the description";
			const multipleCommentaryCopy = `${succeededCommentaries.length} instruments description are updated`;
			platformToast({
				children: succeededCommentaries.length === 1 ? singleCommentaryCopy : multipleCommentaryCopy,
				icon: "Portfolio",
				severity: "success",
				onClick: () => push("PortfolioStudioSettings/InstrumentCustomisation", {}),
			});
		}

		if (commentaryServerError.length > 0) {
			const singleCommentaryCopy = "Something went wrong, unable to generate the commentary";
			const multipleCommentaryCopy = `${commentaryServerError.length} commentary generation went wrong`;
			platformToast({
				children: (
					<ToastContent onClick={() => push("PortfolioStudioSettings/InstrumentCustomisation", {})}>
						{commentaryServerError.length === 1 ? singleCommentaryCopy : multipleCommentaryCopy}
					</ToastContent>
				),
				icon: "Portfolio",
				severity: "error",
			});
		}

		if (missingDataError.length > 0) {
			const singleCommentaryCopy = "The description generation is unavailable for this instrument.";
			const multipleCommentaryCopy = `The description generation is unavailable for ${missingDataError.length} instruments.`;
			platformToast({
				children: (
					<ToastContent onClick={() => push("PortfolioStudioSettings/InstrumentCustomisation", {})}>
						{missingDataError.length === 1 ? singleCommentaryCopy : multipleCommentaryCopy}
					</ToastContent>
				),
				icon: "Portfolio",
				severity: "error",
			});
		}
	});

	// Propagation of instrument customizations to the entities that depend on them. Handled here
	// rather than only inside the instrument customization page: a failed propagation means stale
	// Portfolio Studio entities, so a user sitting in Portfolios or Portfolio Lab needs to know as much
	// as one sitting on the grid, and a page-scoped listener only fires while that page is mounted.
	usePropagationDone(({ propagation }) => {
		// Errors only. These events are customer-scoped, so toasting starts and successes would give
		// every other user in the tenant two notifications per debounce window about work they did
		// not trigger and cannot act on. Start and success are rendered inline in the grid's
		// indicator row instead.
		if (propagation.lastRun?.outcome !== "ERROR") {
			return;
		}
		platformToast({
			children: (
				<ToastContent onClick={() => push("PortfolioStudioSettings/InstrumentCustomisation", {})}>
					Sphere could not apply your instrument changes to the portfolios that depend on them.
				</ToastContent>
			),
			severity: "error",
			autoClose: 15000,
			icon: "Portfolio",
		});
	});

	useEventBus("cash-conversion-to-money-market", (e) => {
		if (e) {
			platformToast({
				children: (
					<ToastContent>
						The composition of <strong>{e}</strong> was converted from cash to money market. Cash is not supported as a
						standalone composition.
					</ToastContent>
				),
				severity: "info",
				autoClose: 15000,
				icon: "Portfolio",
			});
			return;
		}
		platformToast({
			children: "hola",
			severity: "info",
			autoClose: 15000,
			icon: "Portfolio",
		});
	});

	// Toast only: the portfolio page's SmartXStatus refetches the status on the same event.
	useEventBus("smartx-sync", (e) => {
		if (!e.outcome) {
			return;
		}
		const portfolio = <strong>{e.name}</strong>;
		const model = <strong>{e.reportedModelId}</strong>;
		const linked = e.trigger === "LINK";
		const positionsSent = e.positionsSent ?? 0;
		const blocked = e.blockedPositions ?? [];
		const reason = e.reason ? `: ${e.reason}` : ".";
		// An outcome or step the backend added before this client knew it: no toast, or no step clause.
		const stepLabel: string | undefined = e.step ? smartXSyncStepLabels[e.step] : undefined;
		const lead: React.JSX.Element | undefined = {
			SYNCED: (
				<>
					{portfolio} was {linked ? "linked" : "synced"} to SmartX model {model}: {positionsSent}{" "}
					{positionsSent === 1 ? "position" : "positions"} sent.
				</>
			),
			BLOCKED: linked ? (
				<>
					{portfolio} was linked to SmartX model {model}, but the sync was blocked{reason}
				</>
			) : (
				<>
					The sync of {portfolio} to SmartX model {model} was blocked{reason}
				</>
			),
			FAILED: (
				<>
					{linked ? (
						<>
							{portfolio} was linked to SmartX model {model}, but the sync failed
						</>
					) : (
						<>
							The sync of {portfolio} to SmartX model {model} failed
						</>
					)}
					{stepLabel ? ` while ${stepLabel}` : ""}
					{reason}
				</>
			),
		}[e.outcome];
		if (lead == null) {
			return;
		}
		platformToast({
			children: (
				<ToastContent onClick={() => push("PortfolioDetails", { portfolioUid: e.uuid ?? "" })}>
					{lead}
					{blocked.length > 0 && (
						<ul className="list-disc pl-4">
							{keyedBlockedPositions(blocked.slice(0, SMARTX_TOAST_MAX_BLOCKED)).map(({ key, position }) => (
								<li key={key}>
									{position.name}
									{position.alias ? ` (${position.alias})` : ""}
									{position.reason ? `: ${position.reason}` : ""}
								</li>
							))}
							{blocked.length > SMARTX_TOAST_MAX_BLOCKED && (
								<li>and {blocked.length - SMARTX_TOAST_MAX_BLOCKED} more</li>
							)}
						</ul>
					)}
				</ToastContent>
			),
			severity: ({ SYNCED: "info", BLOCKED: "warning", FAILED: "error" } as const)[e.outcome],
			autoClose: 15000,
			icon: "Portfolio",
		});
	});

	return <></>;
}

/**
 * Snackbar notification related to lost/reestablished connectivity.
 *
 * @internal — exported for tests
 */
export function ConnectivityNotification() {
	const { isOnline } = useConnectivity();
	const { t } = useTranslation();

	const [wasOnline, setWasOnline] = useState(isOnline);

	const connectionLostIdRef = useRef<string | number | null>(null);

	useEffect(() => {
		if (isOnline && !wasOnline) {
			if (connectionLostIdRef.current != null) {
				dismissPlatformToast(connectionLostIdRef.current);
				connectionLostIdRef.current = null;
			}
			platformToast({
				icon: "Icon-full-ok",
				children: t("CONNECTION_ESTABLISHED"),
				severity: "success",
			});

			setWasOnline(isOnline);
		} else if (!isOnline && wasOnline) {
			connectionLostIdRef.current = platformToast({
				dismissible: false,
				autoClose: false,
				icon: "Icon-full-alert",
				children: t("CONNECTION_LOST"),
				severity: "error",
			});

			setWasOnline(isOnline);
		}
	}, [isOnline, t, wasOnline]);

	return <></>;
}

export default App;
